// src/views/result.ts
import { html, type TemplateResult, nothing } from 'lit-html';
import { drawRadar } from '../components/radar-chart.js';
import profilesRaw from '../data/profiles.json';
import questionnaireARaw from '../data/questionnaire-a.json';
import questionnaireBRaw from '../data/questionnaire-b.json';
import {
  DIMS, DIM_NAME, DIM_FULL, DIM_EMOJI,
  DIM_VERB_LONG, DIM_VERB_SHORT,
  DIM_FORMAL, DIM_EVERYDAY, DIM_STRENGTH, DIM_BLINDSPOT, DIM_AVOID,
  DIM_SCENARIO, DIM_HABIT,
} from '../constants/dimensions.js';
import { formatDateTime } from '../modules/format.js';
import { pickStrategies } from '../modules/strategies.js';
import type { Store } from '../modules/store.js';
import type {
  Answer, Dim, ProfilesData, Questionnaire,
} from '../types/index.js';

const profiles = profilesRaw as unknown as ProfilesData;
const questionnaireA = questionnaireARaw as unknown as Questionnaire;
const questionnaireB = questionnaireBRaw as unknown as Questionnaire;

const SCENARIO_KEYS = ['newSkill', 'exam', 'presenting'] as const;
type ScenarioKey = typeof SCENARIO_KEYS[number];

function scenarioGuides(preferred: Dim[]): Record<ScenarioKey, string[]> {
  const out: Record<ScenarioKey, string[]> = { newSkill: [], exam: [], presenting: [] };
  for (const d of preferred) {
    for (const k of SCENARIO_KEYS) out[k].push(DIM_SCENARIO[d][k]);
  }
  return out;
}

function microHabitsFor(preferred: Dim[]): string[] {
  if (preferred.length === 1) {
    const main = preferred[0]!;
    const other = DIMS.find(d => d !== main)!;
    return [
      DIM_HABIT[main],
      `每天用 5 分钟做一件"非 ${DIM_NAME[main]}"的事(比如:${DIM_HABIT[other]}),让大脑保持灵活`,
      '完成测试一周后,选一个具体场景试试上面的策略,记下实际感受',
    ];
  }
  if (preferred.length === 2) {
    const [a, b] = preferred as [Dim, Dim];
    return [
      DIM_HABIT[a],
      DIM_HABIT[b],
      `每周挑一个新概念,先用${DIM_NAME[a]}方式吸收一遍,再用${DIM_NAME[b]}方式复盘一遍,让两个通道彼此印证`,
    ];
  }
  return preferred.slice(0, 3).map(d => DIM_HABIT[d]);
}

function plainSummary(preferred: Dim[]): TemplateResult {
  if (preferred.length === 1) {
    const d = preferred[0]!;
    return html`你是<strong>纯 ${DIM_NAME[d]}型</strong>学习者 — 用 ${DIM_NAME[d]} 这一个通道吸收信息时效率最高。和"什么都来一点"的多模态人不同,你专注一种方式反而能学得更深。`;
  }
  if (preferred.length === 2) {
    const [a, b] = preferred as [Dim, Dim];
    return html`你属于<strong>${DIM_NAME[a]}+${DIM_NAME[b]} 双通道</strong>学习者。单独哪一种都不够,需要两种通道配合 — 比如先${DIM_VERB_LONG[b]}再${DIM_VERB_SHORT[a]},才会有"懂了"的感觉。`;
  }
  if (preferred.length === 3) {
    return html`你是<strong>三模态</strong>学习者 — 信息要从三种通道同时进来才记得牢。在大多数学习场景下你都比较从容,但在被迫只用单一通道时容易走神。`;
  }
  const allNames = DIMS.map(d => DIM_NAME[d]).join('、');
  return html`你是难得的<strong>全模态</strong>学习者 — ${allNames}四种通道都能用。这意味着你适应力强,但也容易"什么方法都试一点,什么都不深入"。专注力是你最值得刻意练习的。`;
}

function renderReview(answers: Answer[], qSet: 'A' | 'B'): TemplateResult {
  const data = qSet === 'A' ? questionnaireA : questionnaireB;
  return html`${answers.map(a => {
    const q = data.questions.find(x => x.id === a.questionId);
    if (!q) return nothing;
    return html`
      <div class="review-q">
        <p class="review-qtext"><strong>第 ${q.id} 题:</strong>${q.scenario_zh}</p>
        <ul>
          ${a.selected.map(optId => {
            const o = q.options.find(x => x.id === optId);
            if (!o) return nothing;
            return html`<li>${optId}. ${o.text_zh} <span class="dim-tag">${o.dim}</span></li>`;
          })}
        </ul>
      </div>
    `;
  })}`;
}

interface RenderResultParams {
  store: Store;
}

export function renderResult({ store }: RenderResultParams): TemplateResult {
  const last = store.history[0];
  if (!last) {
    return html`<div class="container"><p>暂无测试记录,<a href="#/intro">立即开始</a>。</p></div>`;
  }
  const { result, questionnaire, answers, completedAt } = last;
  const profile = profiles[result.code];
  const prefSet = new Set<Dim>(result.preferred);
  const nonPreferred = DIMS.filter(d => !prefSet.has(d));
  const scenarios = scenarioGuides(result.preferred);
  const habits = microHabitsFor(result.preferred);

  return html`
    <div class="container result-page">
      <header class="result-card result-hero">
        <div class="result-label">${profile?.label || result.code}</div>
        ${profile?.tagline ? html`<p class="result-tagline">"${profile.tagline}"</p>` : nothing}
        <p class="plain-summary">${plainSummary(result.preferred)}</p>
        <p class="result-meta">${formatDateTime(completedAt)} · 题库 ${questionnaire}</p>
      </header>

      <section class="result-card result-section">
        <h3>四维分数</h3>
        <div class="result-radar"><canvas id="radar"></canvas></div>
        <div class="result-scores">
          ${DIMS.map(d => html`
            <div class=${`score-pill ${prefSet.has(d) ? 'score-pill-pref' : ''}`}>
              <span class="score-dim">${d}</span>
              <span class="score-val">${result.tally[d]}</span>
            </div>
          `)}
        </div>
        <p class="result-meta">总分 ${result.total} · step value ${result.step}</p>
        <p class="radar-help">填充区域越偏向某个角,该维度对你越重要。橙色实心点 = 你的偏好维度。</p>
      </section>

      <section class="result-card result-section">
        <h3>📖 你是怎样学习的</h3>
        ${result.preferred.map(d => html`
          <div class="dim-card">
            <h4>${DIM_EMOJI[d]} ${DIM_FULL[d]}</h4>
            <p class="dim-formal">${DIM_FORMAL[d]}</p>
            <p class="dim-everyday"><span class="dim-tag-inline">日常画像</span>${DIM_EVERYDAY[d]}</p>
          </div>
        `)}
      </section>

      <section class="result-twocol">
        <div class="result-card twocol-card twocol-pos">
          <h3>✨ 你的优势</h3>
          <ul class="twocol-list">
            ${result.preferred.map(d => html`<li><strong>${DIM_NAME[d]}通道</strong> · ${DIM_STRENGTH[d]}</li>`)}
          </ul>
        </div>
        ${nonPreferred.length > 0 ? html`
          <div class="result-card twocol-card twocol-neg">
            <h3>⚠️ 你的盲区</h3>
            <ul class="twocol-list">
              ${nonPreferred.slice(0, 2).map(d => html`<li><strong>${DIM_NAME[d]}通道</strong> · ${DIM_BLINDSPOT[d]}</li>`)}
            </ul>
          </div>
        ` : html`
          <div class="result-card twocol-card twocol-neg">
            <h3>⚠️ 你的盲区</h3>
            <p class="twocol-blank">四种通道都能用,意味着你很少被迫选择 — 而正是这种"随便哪种都行"的心态,可能让每种方式都停在表面。深度来自有意识的选择,而非全面铺开。</p>
          </div>
        `}
      </section>

      <section class="result-card result-section">
        <h3>🎬 三种生活场景下你可以这样做</h3>
        <div class="scenarios">
          <div class="scenario-card">
            <div class="scenario-icon">🎓</div>
            <h4>学一项新技能</h4>
            <ul>${scenarios.newSkill.map(s => html`<li>${s}</li>`)}</ul>
          </div>
          <div class="scenario-card">
            <div class="scenario-icon">📚</div>
            <h4>准备一场考试</h4>
            <ul>${scenarios.exam.map(s => html`<li>${s}</li>`)}</ul>
          </div>
          <div class="scenario-card">
            <div class="scenario-icon">🎤</div>
            <h4>整理一次汇报</h4>
            <ul>${scenarios.presenting.map(s => html`<li>${s}</li>`)}</ul>
          </div>
        </div>
      </section>

      <section class="result-card result-section">
        <h3>💡 给你的学习策略</h3>
        <p class="section-lede">挑几条能立刻行动的来用,不必全部做到。</p>
        <ul class="strategy-list">
          ${pickStrategies(result.code).map(s => html`<li>✓ ${s}</li>`)}
        </ul>
      </section>

      ${nonPreferred.length > 0 ? html`
        <section class="result-card result-section">
          <h3>📵 避免这样学(对你低效)</h3>
          <p class="section-lede">这些是你<strong>不偏好</strong>的通道,如果学习方法长期堆在这里,事倍功半。</p>
          <ul class="avoid-list">
            ${nonPreferred.map(d => html`<li><strong>${DIM_EMOJI[d]} ${DIM_NAME[d]}通道</strong>:${DIM_AVOID[d]}</li>`)}
          </ul>
        </section>
      ` : nothing}

      <section class="result-card result-section result-habits">
        <h3>📅 本周可以开始的 3 个微习惯</h3>
        <p class="section-lede">小到不费意志力,但持续做有效。</p>
        <ol class="habit-list">
          ${habits.map((h, i) => html`<li><span class="habit-num">${i + 1}</span><span>${h}</span></li>`)}
        </ol>
      </section>

      ${profile?.careers ? html`
        <section class="result-card result-section">
          <h3>🎓 学科与职业方向参考</h3>
          <p class="warn">⚠ 仅作探索性参考,不构成升学或求职建议。学习风格只是众多因素之一。</p>
          <div class="career-row"><strong>契合学科:</strong>${profile.careers.subjects.join(' · ')}</div>
          <div class="career-row"><strong>契合职业:</strong>${profile.careers.occupations.join(' · ')}</div>
        </section>
      ` : nothing}

      <section class="result-card result-section">
        <details>
          <summary>📋 答题回顾(看看每题选了什么、贡献到哪个维度)</summary>
          <div class="review">
            ${renderReview(answers, questionnaire)}
          </div>
        </details>
      </section>

      <section class="result-card result-section result-outro">
        <p>🔬 想了解 VARK 模型来源、信效度、争议?<a href="#/about">查看科学性说明 →</a></p>
        <p class="outro-note">学习风格不是定型标签。它是你<em>今天</em>的偏好倾向,会随学科 / 阶段 / 状态变化。隔几个月再测一次,可能会有意外发现。</p>
      </section>

      <nav class="result-actions">
        <button class="btn btn-primary" id="share">📷 生成分享卡片</button>
        <a href="#/intro" class="btn btn-secondary">🔄 重新测试</a>
        <a href="#/" class="btn btn-secondary">🏠 回首页</a>
      </nav>
    </div>
  `;
}

interface ResultCallbacks {
  onShare: () => void | Promise<void>;
}

export function bindResult(root: HTMLElement, store: Store, callbacks: ResultCallbacks): void {
  const last = store.history[0];
  if (!last) return;
  const canvas = root.querySelector<HTMLCanvasElement>('#radar');
  if (canvas) {
    try {
      drawRadar(canvas, last.result.tally, last.result.preferred);
    } catch {
      /* canvas unavailable in jsdom */
    }
  }
  root.querySelector<HTMLButtonElement>('#share')?.addEventListener('click', () => {
    void callbacks.onShare();
  });
}
